import { useMemo } from 'react';
import type { ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  ColumnDef,
  Row,
  RowSelectionState,
  SortingState,
  Updater,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { EmptyState } from './EmptyState';

export interface DataGridAction<TRow, TAction extends string = string> {
  id: TAction;
  label: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  icon?: ReactNode;
  /** Merged onto the mass-action button (e.g. accent styling for one-off actions). */
  buttonClassName?: string;
  /** Returns `true` when the action may run against ALL selected rows. */
  isAllowed?: (rows: TRow[]) => boolean;
  onRun: (rows: TRow[]) => void | Promise<void>;
}

export interface DataGridProps<TRow, TAction extends string = string> {
  columns: ColumnDef<TRow, unknown>[];
  data: TRow[];
  rowId: (row: TRow, index: number) => string;
  /** Actions exposed in the mass-update toolbar that appears on selection. */
  actions?: DataGridAction<TRow, TAction>[];
  /**
   * Allowed actions for a single row. Returning an empty array disables all
   * per-row actions for that row (used for workflow-aware actions).
   */
  allowedActionsForRow?: (row: TRow) => TAction[];
  loading?: boolean;
  emptyState?: ReactNode;
  /** Page meta — controlled pagination (server-side). */
  pagination?: {
    page: number;
    pageSize: number;
    totalRows: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    pageSizeOptions?: number[];
  };
  /** Controlled sort — server-driven. */
  sorting?: {
    state: SortingState;
    onChange: (next: SortingState) => void;
  };
  /** Enables multi-select checkboxes + mass-action bar. */
  selection?: {
    state: RowSelectionState;
    onChange: (next: RowSelectionState) => void;
  };
  /** Callback invoked when the user clicks the "Export CSV" button. */
  onExport?: () => void | Promise<void>;
  /** Optional CTA rendered next to the action bar (e.g. "New product"). */
  primaryAction?: ReactNode;
  /** Renders inline beneath each row when non-null (used for inline edit). */
  rowExpansion?: (row: TRow) => ReactNode | null;
  /** Mark a row with a muted style (e.g. inactive entities). */
  rowMuted?: (row: TRow) => boolean;
  /**
   * Whole-row click (e.g. open detail). Clicks on links, buttons, and form
   * controls inside the row are left to those controls.
   */
  onRowClick?: (row: TRow) => void;
  /** Optional HTML id for each body row (E2E tests). */
  rowDomId?: (row: TRow) => string;
  className?: string;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Responsive, server-driven table built on TanStack Table v8.
 *
 * - Pagination, sorting and selection state are all controlled so the page
 *   can sync them to a GraphQL query and/or the URL.
 * - Mass actions respect a per-row allow-list so that workflow-aware
 *   transitions only surface when every selected row permits them.
 * - Exposes a single "Export CSV" button that the owning page hooks up to a
 *   GraphQL `export*Csv` query.
 */
export function DataGrid<TRow, TAction extends string = string>({
  columns,
  data,
  rowId,
  actions,
  allowedActionsForRow,
  loading,
  emptyState,
  pagination,
  sorting,
  selection,
  onExport,
  primaryAction,
  rowExpansion,
  rowMuted,
  onRowClick,
  rowDomId,
  className,
}: DataGridProps<TRow, TAction>) {
  const resolvedColumns = useMemo(() => {
    if (!selection) return columns;
    const selectionColumn: ColumnDef<TRow, unknown> = {
      id: '__select',
      header: ({ table }) => (
        <CheckboxCell
          checked={table.getIsAllRowsSelected()}
          indeterminate={table.getIsSomeRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
          ariaLabel="Select all rows"
        />
      ),
      cell: ({ row }) => (
        <CheckboxCell
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
          disabled={!row.getCanSelect()}
          ariaLabel="Select row"
        />
      ),
      size: 40,
      enableSorting: false,
    };
    return [selectionColumn, ...columns];
  }, [columns, selection]);

  const table = useReactTable<TRow>({
    data,
    columns: resolvedColumns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row, index) => rowId(row, index),
    manualPagination: true,
    manualSorting: Boolean(sorting),
    pageCount: pagination?.totalPages ?? -1,
    state: {
      sorting: sorting?.state ?? [],
      rowSelection: selection?.state ?? {},
    },
    onSortingChange: sorting
      ? (updater: Updater<SortingState>) => {
          const next =
            typeof updater === 'function' ? updater(sorting.state) : updater;
          sorting.onChange(next);
        }
      : undefined,
    onRowSelectionChange: selection
      ? (updater: Updater<RowSelectionState>) => {
          const next =
            typeof updater === 'function' ? updater(selection.state) : updater;
          selection.onChange(next);
        }
      : undefined,
    enableRowSelection: Boolean(selection),
  });

  const selectedRowModel = selection
    ? table.getSelectedRowModel().rows
    : undefined;
  const selectedRows = useMemo(
    () => (selectedRowModel ? selectedRowModel.map((r) => r.original) : []),
    [selectedRowModel],
  );
  const selectedActions = useMemo(() => {
    if (!actions || selectedRows.length === 0) return [];
    if (!allowedActionsForRow) return actions;
    return actions.filter((action) => {
      if (action.isAllowed) return action.isAllowed(selectedRows);
      return selectedRows.every((row) =>
        allowedActionsForRow(row).includes(action.id),
      );
    });
  }, [actions, selectedRows, allowedActionsForRow]);

  return (
    <section
      className={cn(
        'flex flex-col rounded-2xl border border-gray-200 bg-white shadow-card',
        'dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark',
        className,
      )}
    >
      {(primaryAction || onExport || selectedRows.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-3 dark:border-white/10">
          <div className="flex flex-wrap items-center gap-2">
            {selectedRows.length > 0 ? (
              <>
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {selectedRows.length} selected
                </span>
                {selectedActions.map((action) => (
                  <Button
                    key={action.id}
                    variant={action.variant ?? 'outline'}
                    size="sm"
                    className={action.buttonClassName}
                    leftIcon={action.icon}
                    onClick={() => {
                      void action.onRun(selectedRows);
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
              </>
            ) : (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {pagination
                  ? `${pagination.totalRows.toLocaleString()} total rows`
                  : `${data.length} rows`}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onExport && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Download className="h-4 w-4" aria-hidden="true" />}
                onClick={() => void onExport()}
              >
                Export CSV
              </Button>
            )}
            {primaryAction}
          </div>
        </div>
      )}

      <div className="relative overflow-x-auto">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-900/60">
            <Loader2
              className="h-5 w-5 animate-spin text-brand-500"
              aria-hidden="true"
            />
          </div>
        )}
        <table className="w-full min-w-[600px] border-collapse text-left text-sm">
          <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider dark:bg-gray-900/60 dark:text-gray-400">
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header) => {
                  // TanStack v8: `getCanSort()` is false without `accessorFn` / `accessorKey`.
                  // Server-driven tables still need clickable headers; `getToggleSortingHandler()`
                  // no-ops when `getCanSort()` is false, so call `toggleSorting` directly in that case.
                  const serverSort = sorting !== undefined;
                  const canSortHeader =
                    serverSort &&
                    header.column.id !== '__select' &&
                    header.column.columnDef.enableSorting !== false;
                  const sortDir = header.column.getIsSorted();
                  const ariaSort = !canSortHeader
                    ? undefined
                    : sortDir === 'asc'
                      ? 'ascending'
                      : sortDir === 'desc'
                        ? 'descending'
                        : 'none';
                  const sortHeader = (event: { preventDefault: () => void }) => {
                    const toggle = header.column.getToggleSortingHandler();
                    if (header.column.getCanSort() && toggle) {
                      toggle(event);
                    } else {
                      header.column.toggleSorting(undefined, false);
                    }
                  };
                  const headerLabel = (
                    <>
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {canSortHeader && (
                        <span aria-hidden="true" className="text-gray-400">
                          {sortDir === 'asc' ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : sortDir === 'desc' ? (
                            <ArrowDown className="h-3 w-3" />
                          ) : (
                            <ArrowUpDown className="h-3 w-3" />
                          )}
                        </span>
                      )}
                    </>
                  );
                  return (
                    <th
                      key={header.id}
                      aria-sort={ariaSort}
                      className={cn(
                        'whitespace-nowrap px-4 py-3',
                        canSortHeader && 'select-none',
                      )}
                      style={
                        header.getSize() !== 150
                          ? { width: header.getSize() }
                          : undefined
                      }
                    >
                      {canSortHeader ? (
                        <button
                          type="button"
                          className="flex cursor-pointer items-center gap-1.5 uppercase tracking-wider"
                          onClick={sortHeader}
                        >
                          {headerLabel}
                        </button>
                      ) : (
                        <div className="flex items-center gap-1.5">{headerLabel}</div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-white/10">
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={resolvedColumns.length}
                  className="px-4 py-6 text-center"
                >
                  {emptyState ?? (
                    <EmptyState
                      title="No rows found"
                      description="Adjust your filters or clear the search to see more results."
                    />
                  )}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <RowRenderer<TRow>
                  key={row.id}
                  row={row}
                  rowExpansion={rowExpansion}
                  rowMuted={rowMuted}
                  onRowClick={onRowClick}
                  rowDomId={rowDomId}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && (
        <Pagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSizeOptions={pagination.pageSizeOptions ?? PAGE_SIZE_OPTIONS}
          onPageChange={pagination.onPageChange}
          onPageSizeChange={pagination.onPageSizeChange}
        />
      )}
    </section>
  );
}

function RowRenderer<TRow>({
  row,
  rowExpansion,
  rowMuted,
  onRowClick,
  rowDomId,
}: {
  row: Row<TRow>;
  rowExpansion?: (row: TRow) => ReactNode | null;
  rowMuted?: (row: TRow) => boolean;
  onRowClick?: (row: TRow) => void;
  rowDomId?: (row: TRow) => string;
}) {
  const expansion = rowExpansion?.(row.original) ?? null;
  const muted = Boolean(rowMuted?.(row.original));
  return (
    <>
      <tr
        id={rowDomId?.(row.original)}
        className={cn(
          'transition-colors',
          muted
            ? 'text-gray-400 dark:text-gray-500'
            : 'text-gray-700 dark:text-gray-200',
          row.getIsSelected()
            ? 'bg-brand-50/40 dark:bg-brand-900/20'
            : 'hover:bg-gray-50 dark:hover:bg-gray-800/60',
          onRowClick && 'cursor-pointer',
        )}
        onClick={
          onRowClick
            ? (event) => {
                const target = event.target as HTMLElement | null;
                if (target?.closest('a, button, input, select, textarea, label')) {
                  return;
                }
                onRowClick(row.original);
              }
            : undefined
        }
      >
        {row.getVisibleCells().map((cell) => (
          <td key={cell.id} className="whitespace-nowrap px-4 py-3 align-middle">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
      {expansion && (
        <tr className="bg-gray-50/80 dark:bg-gray-800/40">
          <td
            colSpan={row.getVisibleCells().length}
            className="px-4 py-3"
          >
            {expansion}
          </td>
        </tr>
      )}
    </>
  );
}

function Pagination({
  page,
  pageSize,
  totalPages,
  totalRows,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: {
  page: number;
  pageSize: number;
  totalPages: number;
  totalRows: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}) {
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const start = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(totalRows, page * pageSize);

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 text-xs text-gray-500 dark:border-white/10 dark:text-gray-400">
      <div>
        {totalRows === 0
          ? 'No rows'
          : `${start.toLocaleString()}–${end.toLocaleString()} of ${totalRows.toLocaleString()}`}
      </div>
      <div className="flex items-center gap-2">
        {onPageSizeChange && (
          <label className="flex items-center gap-1">
            <span>Per page</span>
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-gray-200 bg-white px-2 py-1 text-xs shadow-xs dark:border-gray-700 dark:bg-gray-900"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          variant="outline"
          size="sm"
          disabled={!canPrev}
          leftIcon={<ChevronLeft className="h-4 w-4" aria-hidden="true" />}
          onClick={() => onPageChange(page - 1)}
        >
          Prev
        </Button>
        <span className="px-2 font-medium text-gray-700 dark:text-gray-200">
          {page} / {Math.max(totalPages, 1)}
        </span>
        <Button
          variant="outline"
          size="sm"
          disabled={!canNext}
          rightIcon={<ChevronRight className="h-4 w-4" aria-hidden="true" />}
          onClick={() => onPageChange(page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}

function CheckboxCell({
  checked,
  indeterminate,
  onChange,
  disabled,
  ariaLabel,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  ariaLabel: string;
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      ref={(el) => {
        if (el) el.indeterminate = Boolean(indeterminate);
      }}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-400 dark:border-gray-600 dark:bg-gray-900"
    />
  );
}
